<template>
    <div v-if="model">
        <rkm-area no-margin-top title="Filtros" type="fieldset">
            <rkm-form ref="Form" show-legend>
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="loadDependentOptions"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!model.unidadeId"
                            :options="form.infraestruturas"
                            @change="reloadOptions"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.especialidades"
                            @change="reloadOptions"
                            default-option-text="Todas"
                            label="Especialidade"
                            show-default-option
                            text-field="nome"
                            v-model="model.especialidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.usuarios"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.situacoes"
                            default-option-text="Todos"
                            label="Situação da confirmação"
                            show-default-option
                            v-model="model.situacao"
                        />
                    </rkm-col>

                    <rkm-col class="align-self-end" cols="3">
                        <rkm-button color="primary" @click="$refs.Form.validate(search)">
                            Buscar
                        </rkm-button>
                        <rkm-button @click="reset">Limpar</rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-row v-if="searched" class="mb-3 totalizadores">
            <rkm-col v-for="item in totalizerItems" :key="item.key" cols="3">
                <div class="totalizador">
                    <span>{{ item.label }}</span>
                    <strong>{{ totalizadores[item.key] || 0 }}</strong>
                </div>
            </rkm-col>
        </rkm-row>

        <rkm-area no-margin-top title="Resultado">
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :checked="allOptionalColumnsSelected"
                        @change="selectAllOptionalColumns"
                        class="mx-2 mb-2"
                    >
                        TODAS
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="!searched"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-data-table
                :disable-search="true"
                :fields="visibleFields"
                :repository="repositories.confirmacaoConsulta"
                :request-params="requestParams"
                :search-table="searched"
                :tbody-tr-class="rowClass"
                hide-filters
                ref="DataTable"
                sort-by="data"
            >
                <template v-slot:cell(situacaoConfirmacao)="{ value }">
                    {{ situationLabel(value) }}
                </template>

                <template v-slot:cell(origem)="{ value }">
                    {{ originLabel(value) }}
                </template>

                <template v-slot:cell(procedimentos)="{ value }">
                    <div v-for="codigo in splitProcedures(value)" :key="codigo">
                        {{ codigo }}
                    </div>
                </template>

                <template v-slot:cell(actions)="{ item }">
                    <rkm-button
                        v-if="item.podeConfirmar"
                        @click="confirm(item)"
                        authorize="confirmar-ambulatorio-confirmacao-consulta"
                        color="success"
                        small
                        title="Confirmar"
                    >
                        <rkm-icon icon="check" />
                    </rkm-button>
                </template>

            </rkm-data-table>

            <div class="legenda mt-2">
                <span class="legenda-cor"></span>
                Confirmado
                <span class="legenda-cor legenda-cor-cancelado ml-3"></span>
                Cancelado
            </div>
        </rkm-area>

        <ModalPreferenciaImpressao
            @orientation="orientation = $event"
            ref="ModalPreferenciaImpressao"
        />
    </div>
</template>

<script>
import moment from 'moment'
import ModalPreferenciaImpressao from '@/components/geral/impressao/Modal'
import { error, file, formats, repository, stringCase, websocket } from '@/mixins'
import { mapActions } from 'vuex'

export default {
    components: { ModalPreferenciaImpressao },
    computed: {
        allOptionalColumnsSelected() {
            return this.optionalColumns.every(column => this.selectedColumns.includes(column.key))
        },
        optionalColumns() {
            return this.columns.filter(column => !column.fixed)
        },
        requestParams() {
            if (!this.model) return {}

            return {
                periodo: {
                    start: moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidade_id: this.model.unidadeId,
                infraestrutura_id: this.model.infraestruturaId,
                especialidade_id: this.model.especialidadeId,
                usuario_id: this.model.usuarioId,
                situacao: this.model.situacao,
            }
        },
        totalizerItems() {
            return [
                { key: 'pacientes', label: 'Pacientes' },
                { key: 'confirmados', label: 'Confirmados' },
                { key: 'naoConfirmados', label: 'Não confirmados' },
                { key: 'cancelados', label: 'Cancelados' },
            ]
        },
        validations() {
            return { periodo: { required: true } }
        },
        visibleFields() {
            return this.columns.filter(column => column.fixed || this.selectedColumns.includes(column.key))
        },
    },
    created() {
        this.repositories = {
            confirmacaoConsulta: this.getConectaSusRepository('ambulatorio.confirmacaoConsulta'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
        }

        this.load()
    },
    data() {
        return {
            columns: [
                { key: 'paciente', label: 'Paciente', fixed: true, sortable: true },
                { key: 'telefone', label: 'Telefone', fixed: true },
                { key: 'data', label: 'Data', fixed: true, sortable: true, formatter: value => this.formatDate(value) },
                { key: 'horario', label: 'Horário', fixed: true, sortable: true },
                { key: 'unidade', label: 'Unidade', fixed: true, sortable: true },
                { key: 'localAtendimento', label: 'Local de atendimento', fixed: true, sortable: true },
                { key: 'especialidade', label: 'Especialidade', fixed: true, sortable: true },
                { key: 'profissional', label: 'Profissional', fixed: true, sortable: true },
                { key: 'situacaoConfirmacao', label: 'Situação da confirmação', fixed: true, sortable: true },
                { key: 'cns', label: 'CNS' },
                { key: 'prontuario', label: 'Prontuário' },
                { key: 'prontuarioUnidade', label: 'Prontuário da unidade' },
                { key: 'municipio', label: 'Município' },
                { key: 'agendadoEm', label: 'Data/hora do agendamento', formatter: value => this.formatDateTime(value) },
                { key: 'agendadoPor', label: 'Usuário agendador' },
                { key: 'procedimentos', label: 'Procedimentos' },
                { key: 'observacao', label: 'Observação' },
                { key: 'origem', label: 'Origem da confirmação' },
                { key: 'actions', label: 'Ação', class: 'col-min text-center', fixed: true },
            ],
            form: null,
            model: null,
            orientation: 'landscape',
            searched: false,
            optionsTimeout: null,
            realtimeChannel: null,
            realtimeRefreshTimeout: null,
            selectedColumns: [],
            totalizadores: {
                pacientes: 0,
                confirmados: 0,
                naoConfirmados: 0,
                cancelados: 0,
            },
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        selectAllOptionalColumns(selected) {
            this.selectedColumns = selected
                ? this.optionalColumns.map(column => column.key)
                : []
        },
        initRealtime() {
            const customer = this.$store.getters.getCustomer

            if (!this.pusher || !customer || !customer.link || this.realtimeChannel) return

            this.realtimeChannel = `private-${customer.link}-ambulatorio-confirmacao-consulta`
            this.pusher
                .subscribe(this.realtimeChannel)
                .bind('ConfirmacaoConsultaAtualizada', () => this.scheduleRealtimeRefresh())
        },
        scheduleRealtimeRefresh() {
            if (!this.searched) return

            clearTimeout(this.realtimeRefreshTimeout)
            this.realtimeRefreshTimeout = setTimeout(() => this.search(false), 250)
        },
        load() {
            this.setLoading()
            this.repositories.confirmacaoConsulta.create()
                .then(response => {
                    this.form = response.data.form
                    this.model = response.data.model
                    this.model.periodo.start = moment(this.model.periodo.start, 'DD/MM/YYYY').toDate()
                    this.model.periodo.end = moment(this.model.periodo.end, 'DD/MM/YYYY').toDate()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadDependentOptions() {
            this.model.infraestruturaId = null
            this.reloadOptions()
        },
        reloadOptions() {
            this.repositories.confirmacaoConsulta.list({
                ...this.requestParams,
                unidade_id: this.model.unidadeId,
            })
                .then(response => {
                    this.form.infraestruturas = response.data.infraestruturas
                    this.form.especialidades = response.data.especialidades
                    this.form.usuarios = response.data.usuarios
                })
                .catch(this.handleError)
        },
        search(resetPage = true) {
            this.searched = true
            if (resetPage) this.$refs.DataTable.currentPage = 1
            this.$nextTick(() => this.$refs.DataTable.refresh())
            this.refreshTotals()
        },
        refreshTotals() {
            this.repositories.confirmacaoConsulta.totalizadores(this.requestParams)
                .then(response => {
                    this.totalizadores = response.data
                })
                .catch(this.handleError)
        },
        reset() {
            const today = moment().startOf('day').toDate()
            this.model.periodo = { start: today, end: today }
            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.especialidadeId = null
            this.model.usuarioId = null
            this.model.situacao = null
            this.searched = false
            this.totalizadores = { pacientes: 0, confirmados: 0, naoConfirmados: 0, cancelados: 0 }
            this.$refs.Form.resetValidator()
            this.$nextTick(() => this.$refs.DataTable.clearTable())
        },
        print() {
            this.$refs.ModalPreferenciaImpressao.open()
            this.$refs.ModalPreferenciaImpressao.$once('completed', () => {
                const columns = this.visibleFields
                    .filter(column => column.key !== 'actions')
                    .map(column => ({ key: this.camelToSnake(column.key), label: column.label }))
                const request = {
                    ...this.requestParams,
                    selected_cols: columns,
                    orientation: this.orientation,
                }

                this.setLoading()
                this.repositories.impressao.confirmacaoConsulta(request)
                    .then(({ data, headers }) => this.openPrintFile(data, headers['content-type']))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            })
        },
        openPrintFile(data, contentType) {
            const blob = new Blob([data], { type: contentType || 'application/pdf' })
            const url = URL.createObjectURL(blob)

            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 10000)
        },
        confirm(item) {
            const callback = () => this.repositories.confirmacaoConsulta.confirmar(item.agendamentoId)
                .then(({ data }) => {
                    item.situacaoConfirmacao = data ? data.situacao : 'confirmado'
                    item.origem = data ? data.origem : 'manual'
                    item.respondidoEm = data ? data.respondidoEm : null
                    item.podeConfirmar = false
                    this.refreshTotals()
                })
                .catch(this.handleError)

            this.showConfirmation(`Confirma a presença de ${item.paciente}?`, callback)
        },
        situationLabel(value) {
            return {
                confirmado: 'Confirmado',
                naoConfirmado: 'Não confirmado',
                nao_confirmado: 'Não confirmado',
                cancelado: 'Cancelado',
            }[value] || value
        },
        originLabel(value) {
            return {
                whatsapp: 'WhatsApp',
                conectasusCidadao: 'App ConectaSUS Cidadão',
                conectasus_cidadao: 'App ConectaSUS Cidadão',
                manual: 'Manual',
            }[value] || ''
        },
        splitProcedures(value) {
            return value ? value.split('#') : []
        },
        rowClass(item, type) {
            if (!item || type !== 'row') return null

            return {
                confirmado: 'table-success',
                cancelado: 'table-danger',
            }[item.situacaoConfirmacao] || null
        },
    },
    beforeDestroy() {
        clearTimeout(this.optionsTimeout)
        clearTimeout(this.realtimeRefreshTimeout)
    },
    mixins: [error, file, formats, repository, stringCase, websocket],
    mounted() {
        this.initRealtime()
    },
    watch: {
        '$store.state.customer'() {
            this.initRealtime()
        },
        'model.periodo': {
            deep: true,
            handler(value) {
                if (!value || !value.start || !value.end || !this.form) return

                clearTimeout(this.optionsTimeout)
                this.optionsTimeout = setTimeout(() => {
                    this.model.unidadeId = null
                    this.model.infraestruturaId = null
                    this.model.especialidadeId = null
                    this.model.usuarioId = null
                    this.reloadOptions()
                }, 400)
            },
        },
    },
}
</script>

<style scoped>
.totalizador {
    align-items: center;
    background: var(--light);
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 1rem;
}

.totalizador strong {
    font-size: 1.25rem;
}

.legenda {
    align-items: center;
    display: flex;
    gap: 0.5rem;
}

.legenda-cor {
    background: #d4edda;
    border: 1px solid #b1dfbb;
    display: inline-block;
    height: 1rem;
    width: 1.5rem;
}
.legenda-cor-cancelado {
    background: #f8d7da;
    border-color: #f5c6cb;
}
</style>
